import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const APP_NAME = 'Thisway';

// Off-menu pages that still need a sensible tab title.
const TITLE_OVERRIDES = {
  '/login': 'Sign In',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/dashboard': 'Dashboard',
};

const normalizeHref = (href) => {
  if (!href) return null;
  if (href.startsWith('http://') || href.startsWith('https://')) return null;
  return href.startsWith('/') ? href : `/dashboard/${href}`;
};

// Walk the menu tree and return the deepest matching item's name.
// Deepest = longest matching path prefix, so `/dashboard/settings/users`
// matches "Users" not the outer "Settings".
const findTitleInMenu = (items, path) => {
  let bestMatch = null;
  let bestMatchLength = 0;

  const walk = (item) => {
    const itemPath = normalizeHref(item.href);
    if (
      itemPath &&
      (path === itemPath || path.startsWith(itemPath + '/')) &&
      itemPath.length > bestMatchLength
    ) {
      bestMatch = item.name;
      bestMatchLength = itemPath.length;
    }
    if (Array.isArray(item.children)) {
      item.children.forEach(walk);
    }
  };

  (items || []).forEach(walk);
  return bestMatch;
};

const prettify = (segment) =>
  segment
    .split('-')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');

const looksLikeId = (segment) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i.test(segment) || // UUID
  /^[A-Z]?\d+$/.test(segment) ||                            // T001, 123
  /^[0-9a-f]{16,}$/i.test(segment);                         // long hex token

/**
 * Sets `document.title` based on the current route.
 *
 * Lookup order:
 *  1. Off-menu overrides (login, forgot-password, …)
 *  2. Menu items (deepest matching href wins) — pass the menu(s) the layout
 *     already renders so titles stay in sync with the sidebar without
 *     duplicating the source of truth.
 *  3. Pretty-printed last path segment (with id-detection fallback to parent
 *     segment + " Detail").
 */
const useDocumentTitle = (menuItems = []) => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';

    if (TITLE_OVERRIDES[path]) {
      document.title = `${TITLE_OVERRIDES[path]} · ${APP_NAME}`;
      return;
    }

    const titleFromMenu = findTitleInMenu(menuItems, path);
    if (titleFromMenu) {
      document.title = `${titleFromMenu} · ${APP_NAME}`;
      return;
    }

    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      document.title = APP_NAME;
      return;
    }

    let label = segments[segments.length - 1];
    let suffix = '';
    if (looksLikeId(label) && segments.length >= 2) {
      label = segments[segments.length - 2];
      suffix = ' Detail';
    }
    document.title = `${prettify(label)}${suffix} · ${APP_NAME}`;
  }, [location.pathname, menuItems]);
};

export default useDocumentTitle;
